import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PromotionAutomationSettings, useSellerPromotions } from '../../hooks/useSellerPromotions';
import { AutomationRow, RuleField, SaveStatus, SettingsSection, Switch, useAutoSave } from './settingsUi';

// Configuracoes de Promocoes.
//
// Os limites operacionais que existiam aqui (horizonte de ofertas relampago,
// slots por execucao, produtos por execucao, candidatos avaliados, rotacao,
// tipo de regra do bundle, limite por comprador) sairam da tela: todos tinham
// default sensato e ninguem precisa ajusta-los para o produto funcionar. Eles
// vivem agora em sellerPromotionsService.getAutoSettings.
//
// "Duracao da campanha (horas)" foi removido de vez: nunca teve efeito nenhum.
// A duracao agora vem de sellerPromotionsService.campaignDurationDays, que
// respeita o teto de cada marketplace (14 dias na campanha do vendedor do
// Mercado Livre, 30 nos demais).
//
// Tudo salva sozinho; o backend faz merge do patch (updateAutoSettings).

type Settings = Partial<PromotionAutomationSettings>;

function TypeRow({ label, help, checked, onChange }: { label: string; help: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-medium text-gray-800">{label}</p>
        <p className="mt-0.5 text-[11px] text-gray-500">{help}</p>
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

function toNumber(v: unknown): number | null {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function SellerPromotionsSettingsView() {
  const { settings, loading, updateSettings, reconcilePromotions } = useSellerPromotions();
  const [syncingNow, setSyncingNow] = useState(false);
  const [form, setForm] = useState<Settings | null>(null);
  const { status, schedule } = useAutoSave<Settings>(updateSettings);

  useEffect(() => { if (settings && !form) setForm(settings); }, [settings, form]);

  const patch = (next: Settings, immediate = false) => {
    setForm((current) => ({ ...(current || {}), ...next }));
    schedule(next, immediate);
  };

  const syncNow = async () => {
    setSyncingNow(true);
    try {
      const result = await reconcilePromotions();
      const r = result?.reconcile || {};
      const campaigns = result?.catalog?.count != null ? `${result.catalog.count} campanha(s) atualizadas. ` : '';
      toast.success(`${campaigns}${r.checked || 0} anúncio(s) conferidos: ${r.metricsUpdated || 0} com promoções corrigidas, ${r.logsClosed || 0} adesão(ões) encerradas.`);
    } catch (error: any) {
      toast.error(error.message || 'Falha ao sincronizar promoções');
    } finally {
      setSyncingNow(false);
    }
  };

  if (!form) return <p className="text-sm text-gray-500">{loading ? 'Carregando...' : 'Não foi possível carregar.'}</p>;

  const mlModel = form.mlVolumeSubType || 'BNGM';

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><SaveStatus status={status} /></div>

      <SettingsSection
        title="O que roda sozinho"
        help="O preço de cada produto vem da Tabela de preços. Aqui você decide o que o sistema faz com ele."
        right={<button type="button" onClick={syncNow} disabled={syncingNow} className="shrink-0 rounded-md border border-gray-200 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">{syncingNow ? 'Conferindo...' : 'Conferir agora'}</button>}
      >
        <AutomationRow
          title="Conferir promoções"
          help="Atualiza as campanhas disponíveis e confere em quais promoções cada produto está, inclusive mudanças feitas direto na Shopee ou no Mercado Livre."
          enabled={Boolean(form.syncEnabled)}
          onToggle={(v) => patch({ syncEnabled: v }, true)}
          fixedFrequency="1 vez por dia"
          automationKey="promotions-sync"
        />
        <AutomationRow
          title="Entrar em campanhas automaticamente"
          help="Coloca os produtos nas campanhas que já existem, usando o preço da Tabela e sem passar do preço mínimo nem do estoque."
          enabled={Boolean(form.autoJoinEnabled)}
          onToggle={(v) => patch({ autoJoinEnabled: v }, true)}
          automationKey="promotions-join"
        />
        <AutomationRow
          title="Criar campanhas automaticamente"
          help="Quando não houver nenhuma campanha ativa, cria uma dos tipos ligados em 'Tipos de campanha' abaixo."
          enabled={Boolean(form.autoEnabled)}
          onToggle={(v) => patch({ autoEnabled: v }, true)}
          automationKey="promotions-create"
        />
      </SettingsSection>

      <SettingsSection title="Estoque" help="Quanto de cada anúncio fica comprometido com a promoção.">
        <RuleField
          label="Unidades reservadas por anúncio"
          suffix="un."
          integer
          value={toNumber(form.defaultCampaignStock)}
          onChange={(v) => patch({ defaultCampaignStock: v as any })}
        />
        <TypeRow
          label="Somar essas unidades ao estoque antes da oferta relâmpago"
          help="A Shopee e o Mercado Livre travam o estoque assim que o anúncio entra na oferta, mesmo agendada. Ligado, o sistema acrescenta as unidades acima para não travar o que você ainda vende fora da oferta. Desligado, nada muda e o anúncio sem saldo é pulado."
          checked={form.adjustMlLightningStock === true || form.adjustShopeeFlashStock === true}
          onChange={(v) => patch({ adjustMlLightningStock: v, adjustShopeeFlashStock: v }, true)}
        />
      </SettingsSection>

      <SettingsSection title="Tipos de campanha" help="Quais tipos a automação pode criar ou usar em cada marketplace.">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded-lg border border-orange-200 bg-orange-50/30 p-3">
            <p className="text-xs font-bold text-orange-800">Shopee</p>
            <TypeRow label="Oferta relâmpago" help="Ocupa os horários livres que a Shopee divulga." checked={form.createShopeeFlashSale !== false} onChange={(v) => patch({ createShopeeFlashSale: v }, true)} />
            <TypeRow label="Desconto do vendedor" help="Desconto simples nos produtos elegíveis." checked={form.createShopeeDiscount !== false} onChange={(v) => patch({ createShopeeDiscount: v }, true)} />
            <TypeRow label="Leve mais por menos" help="Desconto por quantidade. As faixas por produto ficam na Tabela de preços." checked={form.createShopeeBundleDeal !== false} onChange={(v) => patch({ createShopeeBundleDeal: v }, true)} />
            {form.createShopeeBundleDeal !== false && (
              <RuleField
                label="Desconto padrão do Leve mais por menos"
                suffix="%"
                value={toNumber(form.discountValue)}
                onChange={(v) => patch({ discountValue: v as any })}
                help="Usado quando o produto não tem faixas próprias na Tabela de preços."
              />
            )}
          </div>

          <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/30 p-3">
            <p className="text-xs font-bold text-sky-800">Mercado Livre</p>
            <TypeRow label="Campanha própria" help="Campanha do vendedor. O Mercado Livre limita a 14 dias." checked={form.createMlSellerCampaign !== false} onChange={(v) => patch({ createMlSellerCampaign: v }, true)} />
            <TypeRow label="Desconto por item" help="Desconto individual nos itens elegíveis." checked={form.createMlPriceDiscount !== false} onChange={(v) => patch({ createMlPriceDiscount: v }, true)} />
            <TypeRow label="Leve mais por menos" help="Campanha de volume." checked={form.createMlVolumeCampaign !== false} onChange={(v) => patch({ createMlVolumeCampaign: v }, true)} />
            {form.createMlVolumeCampaign !== false && (
              <div className="space-y-3 rounded-md border border-sky-200 bg-white p-3">
                <label className="block text-xs font-medium text-gray-800">
                  Modelo
                  <select value={mlModel} onChange={(e) => patch({ mlVolumeSubType: e.target.value }, true)} className="mt-1 block w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm font-normal">
                    <option value="BNGM">Leve X, pague Y</option>
                    <option value="BNSP">Compre X com desconto</option>
                    <option value="SPONTH">Desconto na segunda unidade</option>
                  </select>
                </label>
                <RuleField label="Leve (unidades)" integer value={toNumber(form.mlVolumeBuyQty)} onChange={(v) => patch({ mlVolumeBuyQty: v as any })} />
                {mlModel === 'BNGM'
                  ? <RuleField label="Pague (unidades)" integer value={toNumber(form.mlVolumePayQty)} onChange={(v) => patch({ mlVolumePayQty: v as any })} />
                  : <RuleField label="Desconto" suffix="%" value={toNumber(form.mlVolumeDiscountPct)} onChange={(v) => patch({ mlVolumeDiscountPct: v as any })} />}
              </div>
            )}
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}
